import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import HubIcon from '@mui/icons-material/Hub';
import AddIcon from '@mui/icons-material/Add';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';

type InteropConnection = {
  id: number;
  partner_type: 'ehr' | 'national_ehr' | 'payer' | 'lab';
  partner_name: string;
  partner_code: string;
  base_url: string;
  status: 'pending' | 'active' | 'paused' | 'revoked';
  last_sync_at: string | null;
  last_sync_status: string;
  capabilities: string[];
};

const PARTNER_TYPES = [
  { code: 'ehr',          label: 'External EHR' },
  { code: 'national_ehr', label: 'National EHR (e.g. Polish P1)' },
  { code: 'payer',        label: 'Payer / insurer' },
  { code: 'lab',          label: 'Laboratory' },
];

export function PracticeInteropPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'interop'],
    queryFn:  practice.interop.connections.list as () => Promise<InteropConnection[]>,
  });
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<InteropConnection | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const revokeMut = useApiMutation({
    mutationFn: (id: number) => practice.interop.connections.revoke(id),
    onSuccess: () => {
      toast.success('Connection revoked.');
      qc.invalidateQueries({ queryKey: ['practice', 'interop'] });
      setRevoking(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Interoperability mesh"
        subtitle="Connections to external EHRs, national health systems, payers and labs (Pillar 13)."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New connection
          </Button>
        }
      />
      <DataState
        query={q}
        empty={{
          title: 'No connections yet',
          description: 'Connect to external systems to import patient history, register prescriptions, submit claims, or receive lab results.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((c) => (
              <Row key={c.id} connection={c} onRevoke={() => setRevoking(c)} />
            ))}
          </Stack>
        )}
      </DataState>

      {creating && <ConnectionDialog onClose={() => setCreating(false)} />}

      <ConfirmDialog
        open={revoking !== null}
        title={`Revoke ${revoking?.partner_name ?? ''}?`}
        description="The connection stops syncing. Past data already imported into your records is kept."
        destructive
        confirmLabel="Revoke"
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking ? revokeMut.mutateAsync(revoking.id) : Promise.resolve()}
      />
    </>
  );
}

function Row({
  connection, onRevoke,
}: { connection: InteropConnection; onRevoke: () => void }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <HubIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {connection.partner_name}
              </Typography>
              <Chip size="small" label={connection.partner_code}
                    variant="outlined" className="tabular" />
              <Chip size="small"
                    label={connection.status}
                    color={connection.status === 'active' ? 'success'
                           : connection.status === 'pending' ? 'warning'
                           : 'default'}
                    variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'capitalize' }}>
              {connection.partner_type.replace(/_/g, ' ')}
              {connection.last_sync_at && (
                <> · last sync <span className="tabular">{formatDateTime(connection.last_sync_at)}</span></>
              )}
            </Typography>
            {connection.capabilities && connection.capabilities.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {connection.capabilities.map((cap) => (
                  <Chip key={cap} size="small" label={cap.replace(/_/g, ' ')} variant="outlined"
                        sx={{ height: 20, textTransform: 'capitalize' }} />
                ))}
              </Stack>
            )}
          </Box>
          {connection.status === 'active' && (
            <Button size="small" color="error" onClick={onRevoke}>Revoke</Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function ConnectionDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [partnerType, setPartnerType] = useState('ehr');
  const [partnerName, setPartnerName] = useState('');
  const [partnerCode, setPartnerCode] = useState('');
  const [baseUrl, setBaseUrl] = useState('');

  const createMut = useApiMutation({
    mutationFn: () => practice.interop.connections.create({
      partner_type: partnerType,
      partner_name: partnerName,
      partner_code: partnerCode,
      base_url: baseUrl,
    }),
    onSuccess: () => {
      toast.success('Connection requested. Authorize via the partner portal to activate.');
      qc.invalidateQueries({ queryKey: ['practice', 'interop'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = partnerName.trim() && partnerCode.trim()
    && /^https:\/\//.test(baseUrl);

  return (
    <Dialog open onClose={() => !createMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>New interop connection</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField fullWidth select label="Partner type"
                     value={partnerType}
                     onChange={(e) => setPartnerType(e.target.value)}>
            {PARTNER_TYPES.map((t) => (
              <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth required label="Partner name"
                     value={partnerName}
                     onChange={(e) => setPartnerName(e.target.value)} />
          <TextField fullWidth required label="Partner code"
                     value={partnerCode}
                     onChange={(e) => setPartnerCode(e.target.value)}
                     helperText="Short identifier used in audit logs." />
          <TextField fullWidth required label="Base URL"
                     value={baseUrl}
                     onChange={(e) => setBaseUrl(e.target.value)}
                     placeholder="https://fhir.example.com/api/r4"
                     helperText="HTTPS only." />
          <Typography variant="caption" color="text.secondary">
            After creating the connection request, you'll be redirected to the
            partner portal to authorize via OAuth (or upload credentials,
            depending on the integration).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || createMut.isPending}
                onClick={() => createMut.mutate()}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
